import axios from "axios";

import { auth } from "@Firebase/firebase";
import { HttpQuery, PaginatedResult } from "@Types/http";
import { LenderTicket, CreateTicketRequest } from "@Types/tickets";

import { queryClient } from "../App";

export const getTickets = (
  query?: HttpQuery
): (() => Promise<PaginatedResult<LenderTicket[]>>) => {
  return async () => {
    const token = await auth.currentUser?.getIdToken();

    const response = await axios.get(
      `${process.env.REACT_APP_HTTP_URL}/lender-api/v1/ticket/`,
      {
        headers: {
          authorization: `Bearer ${token}`,
          query:
            query && Object.keys(query)?.length
              ? JSON.stringify(query)
              : undefined,
        },
      }
    );

    return {
      docs: response.data.result?.docs ?? [],
      totalDocs: response.data.result?.totalDocs ?? 0,
      totalPages: response.data.result?.totalPages ?? 0,
    };
  };
};

export const getInfiniteTickets = (
  query?: HttpQuery
): (({ pageParam = 1 }) => Promise<PaginatedResult<LenderTicket[]>>) => {
  return async ({ pageParam = 1 }) => {
    const token = await auth.currentUser?.getIdToken();

    if (query?.options) {
      query.options = {
        ...query.options,
        page: pageParam,
      };
    }

    const response = await axios.get(
      `${process.env.REACT_APP_HTTP_URL}/lender-api/v1/ticket/`,
      {
        headers: {
          authorization: `Bearer ${token}`,
          query:
            query && Object.keys(query)?.length
              ? JSON.stringify(query)
              : undefined,
        },
      }
    );

    return response.data.result;
  };
};

export const getTicket = (id: string): (() => Promise<LenderTicket>) => {
  return async () => {
    const token = await auth.currentUser?.getIdToken();
    const response = await axios.get(
      `${process.env.REACT_APP_HTTP_URL}/lender-api/v1/ticket/${id}`,
      {
        headers: {
          authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data.result;
  };
};

export const updateTicket = (): ((
  ticket: LenderTicket
) => Promise<LenderTicket>) => {
  return async (ticket) => {
    const token = await auth.currentUser?.getIdToken();

    const response = await axios.put(
      `${process.env.REACT_APP_HTTP_URL}/lender-api/v1/ticket/${ticket._id}`,
      ticket,
      {
        headers: {
          authorization: `Bearer ${token}`,
        },
      }
    );

    queryClient.setQueryData<LenderTicket>(
      [`ticket-${ticket._id}`],
      () => response.data.result
    );
    return response.data.result;
  };
};

export const createTicket = (): ((
  ticket: CreateTicketRequest
) => Promise<LenderTicket>) => {
  return async (ticket) => {
    const token = await auth.currentUser?.getIdToken();

    const response = await axios.post(
      `${process.env.REACT_APP_HTTP_URL}/lender-api/v1/ticket/`,
      ticket,
      {
        headers: {
          authorization: `Bearer ${token}`,
        },
      }
    );

    return response.data.result;
  };
};
